import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Stack,
  Container,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { useUser } from '../context/UserContext';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { createOrganization, deleteOrganization, modifyOrganization } from '../services/api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  boxShadow: 24,
  p: 4,
};

// Estilos personalizados para las celdas de la tabla
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '16px',
  '&.header': {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    height: '57px',
  }
}));

// Estilo para el contenedor de métricas
const StyledMetricBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: '12px',
  borderRadius: '8px',
  height: '100%',
}));

const Partners = () => {
  const theme = useTheme();
  const { user, updateUser } = useUser();
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [formData, setFormData] = useState({
    organization: '',
    name: '',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteTimer, setDeleteTimer] = useState(null);
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [organizationToModify, setOrganizationToModify] = useState(null);
  const [modifiedOrgData, setModifiedOrgData] = useState({
    name: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isLoading, setIsLoading] = useState(false);
  const HOLD_TIME = 1000; // 1 segundo

  // Cargar organizaciones iniciales
  useEffect(() => {
    if (user?.organizations) {
      setOrganizations(user.organizations);
    }
  }, [user?.organizations]);

  // Solo mostrar el botón si el usuario es admin o partner
  const canCreateUser = user?.rol === 'admin' || user?.rol === 'partner';

  const handleOpen = () => setOpen(true);
  const handleCloseModal = () => {
    setOpen(false);
    setFormData({ organization: '', name: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await createOrganization(formData.name, formData.organization);
      
      // Actualizar el estado local con las nuevas organizaciones
      if (response?.organizations) {
        setOrganizations(response.organizations);
      }
      
      // Actualizar inmediatamente los datos globales del usuario
      if (response) {
        const updatedUserData = {
          ...user,
          ...response, // Aseguramos que todos los campos nuevos se actualicen
          organizations: response.organizations || user.organizations,
          products: response.products || user.products,
        };
        updateUser(updatedUserData);
      }

      // Cerrar el modal y limpiar el formulario
      handleCloseModal();
      
      // Mostrar notificación de éxito
      setSaveSuccess(true);
    } catch (error) {
      console.error('Error al crear la organización:', error);
    }
  };

  const getAvailableParentOrganizations = () => {
    if (!user?.organizations) return [];
    
    // Si el usuario es partner, solo puede seleccionar su propia organización
    if (user.rol === 'partner') {
      return user.organizations.filter(org => org.code === user.organization);
    }
    
    // Si es admin, puede ver todas las organizaciones
    if (user.rol === 'admin') {
      return user.organizations;
    }
    
    return [];
  };

  const handleOpenDeleteModal = (organization) => {
    setOrganizationToDelete(organization);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setOrganizationToDelete(null);
    if (deleteTimer) {
      clearInterval(deleteTimer);
      setDeleteTimer(null);
    }
    setDeleteProgress(0);
  };

  const handleOpenModifyModal = (organization) => {
    setOrganizationToModify(organization);
    setModifiedOrgData({
      name: organization.name
    });
    setOpenModifyModal(true);
  };

  const handleCloseModifyModal = () => {
    setOpenModifyModal(false);
    setOrganizationToModify(null);
    setModifiedOrgData({
      name: ''
    });
  };

  const handleModifySubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await modifyOrganization(
        organizationToModify.code,
        modifiedOrgData.name
      );

      if (response) {
        // Actualizar el estado local y el contexto del usuario con toda la información
        const updatedUserData = {
          ...user,
          ...response, // Aseguramos que todos los campos nuevos se actualicen
          organizations: response.organizations || user.organizations,
          products: response.products || user.products,
        };
        setOrganizations(response.organizations);
        updateUser(updatedUserData);
      }

      handleCloseModifyModal();
      setSnackbar({
        open: true,
        message: 'Partner modificado correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al modificar el partner:', error);
      setSnackbar({
        open: true,
        message: 'Error al modificar el partner',
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    if (!organizationToDelete) return;

    try {
      setIsLoading(true);
      const response = await deleteOrganization(organizationToDelete.code);
      
      if (response) {
        // Actualizar el estado local y el contexto del usuario con toda la información
        const updatedUserData = {
          ...user,
          ...response, // Aseguramos que todos los campos nuevos se actualicen
          organizations: response.organizations || user.organizations,
          products: response.products || user.products,
        };
        setOrganizations(response.organizations);
        updateUser(updatedUserData);
      }

      handleCloseDeleteModal();
      setSnackbar({
        open: true,
        message: 'Partner eliminado correctamente',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error al eliminar el partner',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startDeleteProgress = () => {
    setDeleteProgress(0);
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const progress = (elapsedTime / HOLD_TIME) * 100;
      
      if (progress >= 100) {
        clearInterval(timer);
        handleDelete();
      } else {
        setDeleteProgress(progress);
      }
    }, 10);
    setDeleteTimer(timer);
  };

  const stopDeleteProgress = () => {
    if (deleteTimer) {
      clearInterval(deleteTimer);
      setDeleteTimer(null);
    }
    setDeleteProgress(0);
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ p: 0 }}>
        {/* Header Box */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Partners
            </Typography>
            {canCreateUser && (
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ color: theme.palette.primary.contrastText }} />}
                onClick={handleOpen}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    borderColor: theme.palette.primary.dark,
                  },
                  height: '40px',
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  width: { xs: '100%', sm: 'auto' },
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '& .MuiButton-startIcon': {
                    marginRight: 1,
                    '& svg': {
                      fontSize: '1.2rem'
                    }
                  }
                }}
              >
                Nuevo Cliente
              </Button>
            )}
          </Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Gestiona tus partners y clientes desde un encabezado consistente con el resto del dashboard.
          </Typography>
        </Paper>

        {/* Table Box */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell className="header">Código</StyledTableCell>
                  <StyledTableCell className="header">Nombre</StyledTableCell>
                  <StyledTableCell className="header">Organización Padre</StyledTableCell>
                  {user?.rol === 'admin' && (
                    <StyledTableCell className="header" align="right">Acciones</StyledTableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow
                    key={org.code}
                    sx={{
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <StyledTableCell>{org.code}</StyledTableCell>
                    <StyledTableCell>{org.name}</StyledTableCell>
                    <StyledTableCell>{org.organization_tree || '-'}</StyledTableCell>
                    {user?.rol === 'admin' && (
                      <StyledTableCell align="right" sx={{ width: '100px', padding: '6px' }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModifyModal(org)}
                            sx={{
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.primary.main,
                              border: `1px solid ${theme.palette.divider}`,
                              '&:hover': {
                                backgroundColor: theme.palette.action.selected,
                                borderColor: theme.palette.primary.main,
                              },
                              width: '32px',
                              height: '32px'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteModal(org)}
                            sx={{
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.error.main,
                              border: `1px solid ${theme.palette.error.main}`,
                              '&:hover': {
                                backgroundColor: theme.palette.action.selected,
                                borderColor: theme.palette.error.dark,
                              },
                              width: '32px',
                              height: '32px'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </StyledTableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Modal */}
        <Modal
          open={open}
          onClose={handleCloseModal}
          aria-labelledby="modal-nuevo-usuario"
        >
          <Paper sx={{ ...modalStyle, bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              mb: 2
            }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  textAlign: 'center'
                }}
              >
                Agregar nuevo cliente
              </Typography>
            </Box>

            <Divider sx={{
              mb: 3,
              backgroundColor: theme.palette.divider,
              opacity: 0.5
            }} />

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>Organización</InputLabel>
                  <Select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: theme.palette.background.paper,
                          '& .MuiMenuItem-root': {
                            color: theme.palette.text.primary
                          },
                          '& .MuiMenuItem-root:hover': {
                            bgcolor: theme.palette.action.hover
                          }
                        }
                      }
                    }}
                  >
                    {getAvailableParentOrganizations().map((org) => (
                      <MenuItem
                        key={org.code}
                        value={org.code}
                      >
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Nombre"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.light,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-input': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    onClick={handleCloseModal}
                    variant="contained"
                    sx={{
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.text.secondary,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                        borderColor: theme.palette.text.secondary,
                      },
                      flex: 1,
                      textTransform: 'none',
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckIcon sx={{ color: theme.palette.primary.contrastText }} />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        borderColor: theme.palette.primary.dark,
                      },
                      flex: 1,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        '& svg': {
                          fontSize: '1.2rem'
                        }
                      }
                    }}
                  >
                    Confirmar
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        </Modal>

        {/* Modal de Modificación */}
        <Modal
          open={openModifyModal}
          onClose={handleCloseModifyModal}
          aria-labelledby="modify-modal-title"
        >
          <Box sx={{
            ...modalStyle,
            width: 400,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
          }}>
            <Typography id="modify-modal-title" variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', textAlign: 'center' }}>
              Modificar Partner
            </Typography>
            <Divider sx={{
              mb: 3,
              backgroundColor: theme.palette.divider,
              opacity: 0.5
            }} />
            <form onSubmit={handleModifySubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Nombre"
                  name="name"
                  value={modifiedOrgData.name}
                  onChange={(e) => setModifiedOrgData({ ...modifiedOrgData, name: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: theme.palette.text.primary,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.light,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '&.Mui-focused': {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCloseModifyModal}
                    startIcon={<CloseIcon />}
                    sx={{
                      color: theme.palette.text.primary,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: theme.palette.primary.light,
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckIcon sx={{ color: theme.palette.primary.contrastText }} />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        borderColor: theme.palette.primary.dark,
                      },
                      flex: 1,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        '& svg': {
                          fontSize: '1.2rem'
                        }
                      }
                    }}
                  >
                    Guardar
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Box>
        </Modal>

        {/* Modal de Eliminación */}
        <Modal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          aria-labelledby="delete-modal-title"
        >
          <Box sx={{
            ...modalStyle,
            width: 400,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
          }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Confirmar eliminación
            </Typography>
            <Typography sx={{ color: theme.palette.text.primary, mb: 3, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              ¿Estás seguro de que deseas eliminar el partner{' '}
              <span style={{ color: theme.palette.error.light }}>{organizationToDelete?.name}</span>?
              <br />
              Esta acción no se puede deshacer.
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, mb: 2, fontSize: '0.9rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Mantén presionado el botón para eliminar
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                onClick={handleCloseDeleteModal}
                variant="outlined"
                disabled={isLoading}
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Cancelar
              </Button>
              <Box sx={{ position: 'relative' }}>
                <Button
                  onMouseDown={startDeleteProgress}
                  onMouseUp={stopDeleteProgress}
                  onMouseLeave={stopDeleteProgress}
                  onTouchStart={startDeleteProgress}
                  onTouchEnd={stopDeleteProgress}
                  variant="contained"
                  disabled={isLoading}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: theme.palette.error.main,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    color: theme.palette.getContrastText(theme.palette.error.main),
                    '&:hover': {
                      bgcolor: theme.palette.error.dark
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${deleteProgress}%`,
                      height: '100%',
                      backgroundColor: theme.palette.error.dark,
                      transition: 'width 0.01s linear'
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Eliminando...
                    </>
                  ) : (
                    'Mantener para eliminar'
                  )}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Snackbar de éxito */}
        <Snackbar 
          open={saveSuccess} 
          autoHideDuration={3000} 
          onClose={() => setSaveSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success" sx={{ backgroundColor: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) }}>
            Cliente creado exitosamente
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Partners;
